import { d as __rest, _ as __assign } from './_tslib-3301712a.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useState, useEffect, useRef, memo } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import Loading, { LoadingColor, LoadingScale } from 'bloko/blocks/loading';
import useBreakpoint, { Breakpoint } from 'bloko/common/hooks/useBreakpoint';
import modalHelper from 'bloko/common/modalHelper';

var VIEWER_NODE_ID = 'chatikIntegration_viewer';

/**
 * Хук для определения, зазумлена ли страница на тач-устройствах.
 * @param isMobile - булеан, определяющий, нужно ли активировать зум-трекинг (только для мобилок)
 * @returns boolean — true, если страница зазумлена, false — если нет
 */
function useZoomDetection(isMobile) {
    var _a = useState(false), isZoomed = _a[0], setIsZoomed = _a[1];
    useEffect(function () {
        if (!isMobile || typeof window === 'undefined' || !window.visualViewport) {
            return undefined;
        }
        var handleZoomChange = function () {
            var _a;
            var currentScale = ((_a = window.visualViewport) === null || _a === void 0 ? void 0 : _a.scale) || 1;
            var scale = parseFloat(currentScale.toFixed(2));
            setIsZoomed(scale !== 1);
        };
        handleZoomChange();
        window.visualViewport.addEventListener('resize', handleZoomChange);
        return function () {
            var _a;
            (_a = window.visualViewport) === null || _a === void 0 ? void 0 : _a.removeEventListener('resize', handleZoomChange);
        };
    }, [isMobile]);
    return isZoomed;
}

function styleInject(css, ref) {
  if (ref === void 0) ref = {};
  var insertAt = ref.insertAt;
  if (!css || typeof document === 'undefined') {
    return;
  }
  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';
  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }
  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z$2 = ".icon-button {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  overflow: hidden;\n}\n.icon-button_small {\n  width: 40px;\n  height: 40px;\n}\n.icon-button_medium {\n  width: 48px;\n  height: 48px;\n}\n.icon-button_default svg {\n  color: #000;\n  stroke: #000;\n}\n.icon-button_default svg:hover {\n  background-color: #f4f6fb;\n}\n.icon-button_contrast svg {\n  color: #FFF;\n  stroke: #FFF;\n}\n:global(.focus-visible) .icon-button {\n  outline: 4px solid #0070ff7a;\n}\n.icon-button_disabled {\n  pointer-events: none;\n}\n.icon-button_disabled svg {\n  opacity: 0.3;\n}\n.icon-button-element {\n  display: flex;\n  background: transparent;\n  border: none;\n}\n.icon-button-element:active {\n  transform: scale(0.96);\n  transition-duration: 100ms;\n  transition-timing-function: cubic-bezier(0.25, 0.1, 0.25, 1);\n}\n.icon-button-element:focus {\n  outline: none;\n}\n";
styleInject(css_248z$2);

var IconButton = function (_a) {
    var _b = _a.Element, Element = _b === void 0 ? 'button' : _b, className = _a.className, size = _a.size, view = _a.view, disabled = _a.disabled, rest = __rest(_a, ["Element", "className", "size", "view", "disabled"]);
    return (jsx("div", { className: classNames('icon-button', {
            'icon-button_small': size === 'small',
            'icon-button_medium': size === 'medium',
            'icon-button_small_default': view === 'default',
            'icon-button_small_contrast': view === 'contrast',
            'icon-button_disabled': disabled,
        }), children: jsx(Element, __assign({ className: classNames(className, 'icon-button-element') }, rest)) }));
};

var COLORS = {
    default: '#000',
    contrast: '#fff',
};

var css_248z$1 = ".ci-icon {\n  overflow: hidden;\n  stroke-width: 2;\n  stroke-linecap: round;\n  stroke-linejoin: round;\n}\n";
styleInject(css_248z$1);

var CloseIcon = function (_a) {
    var view = _a.view;
    return (jsx("svg", { stroke: COLORS[view], className: "ci-icon", width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: jsx("path", { d: "M18 6L6 18M6 6L18 18" }) }));
};

var DownloadIcon = function (_a) {
    var view = _a.view;
    return (jsx("svg", { width: 24, height: 24, viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: jsxs("g", { children: [jsx("path", { d: "M17.446 3.13411C16.1399 2.88803 14.3925 2.8 12 2.8C9.60754 2.8 7.86012 2.88803 6.55397 3.13411C5.26535 3.3769 4.58469 3.74215 4.16342 4.16342C3.74215 4.58469 3.3769 5.26535 3.13411 6.55397C2.88803 7.86012 2.8 9.60754 2.8 12L1 12C1 2.375 2.375 1 12 1C21.625 1 23 2.375 23 12L21.2 12C21.2 9.60754 21.112 7.86012 20.8659 6.55397C20.6231 5.26535 20.2579 4.58469 19.8366 4.16342C19.4153 3.74215 18.7347 3.3769 17.446 3.13411Z", fill: COLORS[view] }), jsx("path", { d: "M6.36362 16.3635L12 22L17.6364 16.3636L16.3636 15.0908L12.9 18.5544L12.9 8.72719L11.1 8.72719L11.1 18.5544L7.63642 15.0907L6.36362 16.3635Z", fill: COLORS[view] })] }) }));
};

var RotateIcon = function (_a) {
    var view = _a.view;
    return (jsx("svg", { className: "ci-icon", width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: jsx("g", { children: jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M5.10005 14.2222C5.10005 17.1264 6.03955 18.7144 7.13477 19.6271C8.30744 20.6043 10.0132 21.1 11.9778 21.1C13.9424 21.1 15.6482 20.6043 16.8209 19.6271C17.9161 18.7144 18.8556 17.1264 18.8556 14.2222L20.6556 14.2222C20.6556 17.5608 19.5497 19.6961 17.9732 21.0099C16.3765 22.3405 14.2108 22.9 11.9778 22.9C9.74482 22.9 7.57915 22.3405 5.98244 21.0099C4.4059 19.6961 3.30005 17.5608 3.30005 14.2222C3.30005 10.8837 4.4059 8.74841 5.98244 7.43463C7.57915 6.10403 9.74482 5.54447 11.9778 5.54447L14.2495 5.54447L11.3414 2.63642L12.6142 1.36363L17.6951 6.44446L12.6143 11.5253L11.3415 10.2525L14.2495 7.34447L11.9778 7.34447C10.0132 7.34447 8.30744 7.8402 7.13477 8.81743C6.03955 9.73011 5.10005 11.3181 5.10005 14.2222Z", fill: COLORS[view] }) }) }));
};

var ZoomInIcon = function (_a) {
    var view = _a.view;
    return (jsx("svg", { className: "ci-icon", width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: jsxs("g", { children: [jsx("path", { d: "M10.1 16L10.1 11.9L6 11.9V10.1L10.1 10.1L10.1 6H11.9L11.9 10.1L16 10.1L16 11.9L11.9 11.9L11.9 16L10.1 16Z", fill: COLORS[view] }), jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M11 1C3.5 1 1 6 1 11C1 16 3.5 21 11 21C13.9603 21 16.1417 20.221 17.6978 18.9705L21.7236 22.9964L22.9964 21.7236L18.9624 17.6896C20.4151 15.8369 21 13.4185 21 11C21 6 18.5 1 11 1ZM4.5703 5.27733C3.36163 6.72773 2.8 8.76743 2.8 11C2.8 13.2326 3.36163 15.2723 4.5703 16.7227C5.73084 18.1153 7.68287 19.2 11 19.2C14.3171 19.2 16.2692 18.1153 17.4297 16.7227C18.6384 15.2723 19.2 13.2326 19.2 11C19.2 8.76743 18.6384 6.72773 17.4297 5.27733C16.2692 3.88468 14.3171 2.8 11 2.8C7.68287 2.8 5.73084 3.88468 4.5703 5.27733Z", fill: COLORS[view] })] }) }));
};

var ZoomOutIcon = function (_a) {
    var view = _a.view;
    return (jsx("svg", { className: "ci-icon", width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: jsxs("g", { children: [jsx("path", { d: "M6 10.1V11.9L16 11.9L16 10.1L6 10.1Z", fill: COLORS[view] }), jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M11 1C3.5 1 1 6 1 11C1 16 3.5 21 11 21C13.9603 21 16.1417 20.221 17.6978 18.9705L21.7236 22.9964L22.9964 21.7236L18.9624 17.6896C20.4151 15.8369 21 13.4185 21 11C21 6 18.5 1 11 1ZM4.5703 5.27733C3.36163 6.72773 2.8 8.76743 2.8 11C2.8 13.2326 3.36163 15.2723 4.5703 16.7227C5.73084 18.1153 7.68287 19.2 11 19.2C14.3171 19.2 16.2692 18.1153 17.4297 16.7227C18.6384 15.2723 19.2 13.2326 19.2 11C19.2 8.76743 18.6384 6.72773 17.4297 5.27733C16.2692 3.88468 14.3171 2.8 11 2.8C7.68287 2.8 5.73084 3.88468 4.5703 5.27733Z", fill: COLORS[view] })] }) }));
};

var SwipeListener = function (_a) {
    var onSwipeUp = _a.onSwipeUp, onSwipeDown = _a.onSwipeDown, _b = _a.threshold, threshold = _b === void 0 ? 100 : _b;
    var touchStartY = useRef(null);
    var touchEndY = useRef(null);
    var touchStartX = useRef(null);
    var touchEndX = useRef(null);
    var isSingleTouch = useRef(true);
    useEffect(function () {
        var handleTouchStart = function (e) {
            isSingleTouch.current = e.touches.length === 1;
            if (!isSingleTouch.current) {
                touchStartY.current = null;
                touchEndY.current = null;
                touchStartX.current = null;
                touchEndX.current = null;
                return;
            }
            touchStartY.current = e.touches[0].clientY;
            touchStartX.current = e.touches[0].clientX;
        };
        var handleTouchMove = function (e) {
            if (!isSingleTouch.current || e.touches.length !== 1) {
                return;
            }
            touchEndY.current = e.touches[0].clientY;
            touchEndX.current = e.touches[0].clientX;
        };
        var handleTouchEnd = function () {
            if (!isSingleTouch.current) {
                return;
            }
            if (touchStartY.current === null || touchEndY.current === null ||
                touchStartX.current === null || touchEndX.current === null) {
                return;
            }
            var deltaY = touchStartY.current - touchEndY.current;
            var deltaX = touchStartX.current - touchEndX.current;
            // обрабатываем только если вертикальный свайп больше, чем горизонтальный
            if (Math.abs(deltaY) > Math.abs(deltaX)) {
                if (deltaY > threshold) {
                    onSwipeUp === null || onSwipeUp === void 0 ? void 0 : onSwipeUp();
                }
                else if (deltaY < -threshold) {
                    onSwipeDown === null || onSwipeDown === void 0 ? void 0 : onSwipeDown();
                }
            }
            touchStartY.current = null;
            touchEndY.current = null;
            touchStartX.current = null;
            touchEndX.current = null;
            isSingleTouch.current = true;
        };
        window.addEventListener('touchstart', handleTouchStart);
        window.addEventListener('touchmove', handleTouchMove);
        window.addEventListener('touchend', handleTouchEnd);
        return function () {
            window.removeEventListener('touchstart', handleTouchStart);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleTouchEnd);
        };
    }, [onSwipeUp, onSwipeDown, threshold]);
    return null;
};

var useCloseHandlers = function (ref, close) {
    useEffect(function () {
        var handleKeyDown = function (e) {
            if (e.key === 'Escape') {
                close();
            }
        };
        // при клике на картинку в чатике фокус переходит внутрь айфрейма
        // нужно вернуть фокус на вьевер снаружи, чтобы работал обработчик keydown
        if (ref.current) {
            ref.current.tabIndex = -1;
            ref.current.focus();
        }
        window.addEventListener('keydown', handleKeyDown);
        return function () {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [close, ref]);
    useEffect(function () {
        var handleClick = function (e) {
            if (e.target === ref.current) {
                close();
            }
        };
        window.addEventListener('click', handleClick);
        return function () {
            window.removeEventListener('click', handleClick);
        };
    }, [ref, close]);
};

var DESKTOP_PADDINGS = 86;
var useImageDimensions = function (_a) {
    var contentRef = _a.contentRef, originalWidth = _a.originalWidth, originalHeight = _a.originalHeight, isMobile = _a.isMobile, rotation = _a.rotation, zoom = _a.zoom;
    var _b = useState({
        width: 0,
        height: 0,
    }), maxDimensions = _b[0], setMaxDimensions = _b[1];
    useEffect(function () {
        var container = contentRef.current;
        if (!container) {
            return undefined;
        }
        var normalizedRotation = ((rotation % 360) + 360) % 360;
        // если изображение повернуто на 90 или 270 градусов, то нужно поменять ширину с высотой местами
        var isRotated = normalizedRotation % 180 !== 0;
        var aspectRatio = isRotated ? originalHeight / originalWidth : originalWidth / originalHeight;
        var observer = new ResizeObserver(function (entries) {
            var entry = entries[0];
            var containerWidth = entry.contentRect.width;
            var containerHeight = entry.contentRect.height;
            var calculatedWidth = Math.min(
            // 2 горизонтальных паддинга на десктопе
            containerWidth - (isMobile ? 0 : DESKTOP_PADDINGS * 2), containerHeight * aspectRatio);
            var calculatedHeight = Math.min(
            // 2 вертикальных паддинга на десктопе
            containerHeight - (isMobile ? 0 : DESKTOP_PADDINGS * 2), containerWidth / aspectRatio);
            calculatedWidth = Math.min(calculatedWidth, originalWidth);
            calculatedHeight = Math.min(calculatedHeight, originalHeight);
            if (zoom !== 1) {
                calculatedWidth *= zoom;
                calculatedHeight *= zoom;
            }
            setMaxDimensions({
                width: isRotated ? calculatedHeight : calculatedWidth,
                height: isRotated ? calculatedWidth : calculatedHeight,
            });
        });
        observer.observe(container);
        return function () { return observer.disconnect(); };
    }, [contentRef, originalHeight, originalWidth, isMobile, rotation, zoom]);
    return maxDimensions;
};

var updateTransform = function (el, x, y) {
    el.style.transform = "translate(".concat(x, "px, ").concat(y, "px)");
};
var clamp = function (value, min, max) { return Math.max(min, Math.min(value, max)); };
var getMaxOffsets = function (img, zoom) {
    var viewportWidth = window.innerWidth;
    var viewportHeight = window.innerHeight;
    var rect = img.getBoundingClientRect();
    var width = rect.width * zoom;
    var height = rect.height * zoom;
    var maxX = Math.max(0, (width - viewportWidth) / 2);
    var maxY = Math.max(0, (height - viewportHeight) / 2);
    return { maxX: maxX, maxY: maxY };
};
var useImageDrag = function (_a) {
    var imgRef = _a.imgRef, isDesktop = _a.isDesktop, zoom = _a.zoom, rotation = _a.rotation;
    var isDragging = useRef(false);
    var startX = useRef(0);
    var startY = useRef(0);
    var translate = useRef({ x: 0, y: 0 });
    var prevZoom = useRef(zoom);
    var prevRotation = useRef(rotation);
    useEffect(function () {
        var img = imgRef.current;
        if (!img || !isDesktop || zoom <= 1) {
            return undefined;
        }
        var onMouseDown = function (e) {
            isDragging.current = true;
            startX.current = e.clientX;
            startY.current = e.clientY;
            img.style.cursor = 'grabbing';
        };
        var onMouseMove = function (e) {
            if (!isDragging.current) {
                return;
            }
            var dx = e.clientX - startX.current;
            var dy = e.clientY - startY.current;
            startX.current = e.clientX;
            startY.current = e.clientY;
            var newX = translate.current.x + dx;
            var newY = translate.current.y + dy;
            var _a = getMaxOffsets(img, zoom), maxX = _a.maxX, maxY = _a.maxY;
            translate.current.x = clamp(newX, -maxX, maxX);
            translate.current.y = clamp(newY, -maxY, maxY);
            requestAnimationFrame(function () { return updateTransform(img, translate.current.x, translate.current.y); });
        };
        var onMouseUp = function () {
            isDragging.current = false;
            img.style.cursor = 'grab';
        };
        img.style.cursor = 'grab';
        img.style.userSelect = 'none';
        img.addEventListener('mousedown', onMouseDown);
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
        return function () {
            img.removeEventListener('mousedown', onMouseDown);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [imgRef, isDesktop, zoom, rotation]);
    useEffect(function () {
        var img = imgRef.current;
        if (!img) {
            return;
        }
        if (zoom !== prevZoom.current || rotation !== prevRotation.current) {
            translate.current = { x: 0, y: 0 };
            updateTransform(img, 0, 0);
            prevZoom.current = zoom;
            prevRotation.current = rotation;
        }
    }, [zoom, rotation, imgRef]);
};

var useViewerHistory = function (onClose) {
    useEffect(function () {
        window.history.pushState(null, '', window.location.href);
        var handlePopState = function () { return onClose(); };
        window.addEventListener('popstate', handlePopState);
        return function () {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [onClose]);
};

var downloadFile = function (url) {
    if (typeof window !== 'undefined') {
        window.open(url);
    }
};

var ZOOM_STEP = 0.2;
var MIN_ZOOM = ZOOM_STEP * 2;
var MAX_ZOOM = ZOOM_STEP * 3 + 1;
var updateZoomValue = function (currentZoom, direction) {
    var newZoom = direction === 'in' ? currentZoom + ZOOM_STEP : currentZoom - ZOOM_STEP;
    return Math.round(newZoom * 10) / 10;
};

var css_248z = ".viewer-overlay {\n  position: fixed;\n  inset: 0 0;\n  display: flex;\n  flex-direction: column;\n  width: 100%;\n  height: 100%;\n  background: #000000E5;\n  z-index: 100000;\n}\n.viewer-header {\n  position: relative;\n  top: 0px;\n  transition: top 0.3s;\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  padding: 24px;\n  width: 100%;\n  box-sizing: border-box;\n  height: 82px;\n  z-index: 1;\n  background: rgba(0, 0, 0, 0.9);\n}\n@media (max-width: 1019px) {\n  .viewer-header {\n    justify-content: flex-end;\n    height: 64px;\n    padding: 8px 16px;\n  }\n}\n.viewer-header_hidden {\n  top: -34px;\n}\n.viewer-header_hidden > * {\n  visibility: hidden;\n}\n.viewer-content {\n  display: flex;\n  flex-grow: 1;\n}\n.viewer-actions {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  column-gap: 12px;\n}\n.viewer-caption {\n  flex: 1;\n  overflow: hidden;\n  color: #fff;\n  font-family: hh sans, Arial, sans-serif;\n  font-style: normal;\n  font-weight: 400;\n  line-height: 22px;\n}\n.viewer-footer .viewer-caption {\n  text-align: center;\n}\n.viewer-title {\n  font-size: 16px;\n  line-height: 22px;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\n@media (max-width: 1019px) {\n  .viewer-title {\n    font-size: 12px;\n    line-height: 18px;\n  }\n}\n.viewer-subtitle {\n  margin-top: 4px;\n  font-size: 14px;\n  line-height: 20px;\n}\n@media (max-width: 1019px) {\n  .viewer-subtitle {\n    font-size: 10px;\n    line-height: 14px;\n    margin-top: 0;\n  }\n}\n.viewer-image-wrapper {\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  max-width: 100%;\n  max-height: 100%;\n}\n.viewer-image-wrapper_hidden {\n  opacity: 0;\n}\n.viewer-image {\n  display: block;\n  object-fit: contain;\n  user-select: none;\n  -webkit-user-select: none;\n  transition: transform 0.3s, height 0.3s, width 0.3s;\n}\n.viewer-loader,\n.viewer-image-wrapper {\n  position: absolute;\n  top: 50%;\n  left: 50%;\n  transform: translate(-50%, -50%);\n}\n.viewer-loader svg {\n  height: 64px;\n  width: 64px;\n}\n@media (max-width: 1019px) {\n  .viewer-loader svg {\n    height: 48px;\n    width: 48px;\n  }\n}\n.viewer-footer {\n  position: relative;\n  bottom: 0px;\n  transition: bottom 0.3s;\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  background: #000000e6;\n  padding: 12px 16px;\n  z-index: 1;\n}\n@media (min-width: 1020px) {\n  .viewer-footer {\n    display: none;\n  }\n}\n.viewer-footer_hidden {\n  bottom: -42px;\n}\n.viewer-footer_hidden > * {\n  visibility: hidden;\n}\n.viewer-zoom-state {\n  color: #fff;\n  font-family: hh sans, Arial, sans-serif;\n  font-size: 18px;\n  font-style: normal;\n  font-weight: 400;\n  line-height: 26px;\n}\n";
styleInject(css_248z);

var POLLING_INTERVAL = 3000;
var MAX_ATTEMPTS = 5;
var Viewer = memo(function (_a) {
    var title = _a.title, imageUrl = _a.imageUrl, imageOriginalWidth = _a.imageOriginalWidth, imageOriginalHeight = _a.imageOriginalHeight, subtitle = _a.subtitle, onClose = _a.onClose, onImageLoadingError = _a.onImageLoadingError;
    var _b = useState(true), loading = _b[0], setLoading = _b[1];
    var _c = useState(0), loadAttempt = _c[0], setLoadAttempt = _c[1];
    var _d = useState(1), zoom = _d[0], setZoom = _d[1];
    var _e = useState(false), isControlsHidden = _e[0], hideControls = _e[1];
    var _f = useState(0), rotation = _f[0], setRotation = _f[1];
    var timerRef = useRef();
    var contentRef = useRef(null);
    var bodyRef = useRef(null);
    var dragElementRef = useRef(null);
    var imageRef = useRef(null);
    var breakpoint = useBreakpoint();
    var isMobile = breakpoint === Breakpoint.XS || breakpoint === Breakpoint.S;
    var isPageZoomed = useZoomDetection(isMobile);
    var dimensions = useImageDimensions({
        contentRef: contentRef,
        isMobile: isMobile,
        rotation: rotation,
        zoom: zoom,
        originalWidth: imageOriginalWidth,
        originalHeight: imageOriginalHeight,
    });
    useEffect(function () { return function () {
        // @ts-expect-error
        clearTimeout(timerRef.current);
    }; }, []);
    useEffect(function () {
        modalHelper.disableScroll();
        return function () {
            modalHelper.enableScroll();
        };
    }, []);
    useEffect(function () {
        hideControls(isPageZoomed);
    }, [isPageZoomed]);
    useCloseHandlers(bodyRef, onClose);
    useImageDrag({ imgRef: dragElementRef, isDesktop: !isMobile, zoom: zoom, rotation: rotation });
    useViewerHistory(onClose);
    var handleError = function () {
        if (loadAttempt >= MAX_ATTEMPTS) {
            setLoading(false);
            onImageLoadingError === null || onImageLoadingError === void 0 ? void 0 : onImageLoadingError();
            onClose();
            return;
        }
        timerRef.current = setTimeout(function () {
            setLoadAttempt(function (attempt) { return attempt + 1; });
        }, POLLING_INTERVAL);
    };
    var handleLoad = function () {
        setLoading(false);
    };
    var handleRotate = function () {
        setRotation(function (prev) { return prev + 90; });
    };
    var onImageClick = function () {
        if (!isMobile) {
            return;
        }
        hideControls(!isControlsHidden);
    };
    var imageCaption = title || subtitle ? (jsxs("div", { className: "viewer-caption", children: [title && jsx("div", { className: "viewer-title", children: title }), subtitle && jsx("div", { className: "viewer-subtitle", children: subtitle })] })) : null;
    var closeButton = (jsx(IconButton, { size: "medium", view: "contrast", onClick: onClose, children: jsx(CloseIcon, { view: "contrast" }) }));
    var downloadButton = (jsx(IconButton, { size: "medium", view: "contrast", onClick: function () { return downloadFile(imageUrl); }, children: jsx(DownloadIcon, { view: "contrast" }) }));
    var rotateIcon = (jsx(IconButton, { size: "medium", view: "contrast", onClick: handleRotate, disabled: loading, children: jsx(RotateIcon, { view: "contrast" }) }));
    return ReactDOM.createPortal(jsxs("div", { ref: contentRef, className: "viewer-overlay", id: VIEWER_NODE_ID, children: [jsxs("div", { className: classNames('viewer-header', { 'viewer-header_hidden': isControlsHidden }), children: [!isMobile && imageCaption, jsxs("div", { className: "viewer-actions", children: [!isMobile && (jsxs(Fragment, { children: [!loading && jsxs("div", { className: "viewer-zoom-state", children: [zoom * 100, "%"] }), jsx(IconButton, { size: "medium", view: "contrast", disabled: loading || zoom <= MIN_ZOOM, onClick: function () { return setZoom(updateZoomValue(zoom, 'out')); }, children: jsx(ZoomOutIcon, { view: "contrast" }) }), jsx(IconButton, { size: "medium", view: "contrast", disabled: loading || zoom >= MAX_ZOOM, onClick: function () { return setZoom(updateZoomValue(zoom, 'in')); }, children: jsx(ZoomInIcon, { view: "contrast" }) })] })), !isMobile && rotateIcon, !isMobile && downloadButton, closeButton] })] }), jsxs("div", { ref: bodyRef, className: "viewer-content", children: [jsx("div", { className: classNames('viewer-image-wrapper', { 'viewer-image-wrapper_hidden': loading }), children: jsx("div", { ref: dragElementRef, children: jsx("img", { "data-qa": "file-image-preview", className: "viewer-image", src: imageUrl, ref: imageRef, style: __assign(__assign({}, dimensions), { transform: "rotate(".concat(rotation, "deg)") }), onError: handleError, onLoad: handleLoad, onClick: onImageClick, draggable: false }, loadAttempt) }) }), loading && (jsx("div", { className: "viewer-loader", children: jsx(Loading, { initial: LoadingColor.White, scale: LoadingScale.Medium }) }))] }), isMobile && (jsxs("div", { className: classNames('viewer-footer', { 'viewer-footer_hidden': isControlsHidden }), children: [rotateIcon, imageCaption, downloadButton] })), isMobile && jsx(SwipeListener, { onSwipeUp: onClose, onSwipeDown: onClose })] }), document.body);
});
Viewer.displayName = 'Viewer';

export { Viewer as V, styleInject as s };
//# sourceMappingURL=index-852b2958.js.map
